import { useState, useMemo, useCallback } from 'react';
import { toastr } from 'react-redux-toastr';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from 'react-query';

// Components
import {
  useTableFilterSortSearchManager,
  useTableManageColumns,
  useTableFilterSortData,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';

// Hooks
import useCertificationsQuery from '../../hooks/data-fetching/useCertificationsQuery';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useDownloadDocumentMutation from '../../hooks/data-modifying/useDownloadDocumentMutation';
import useGenerate59AMutation from '../../hooks/data-modifying/useGenerate59AMutation';
import useGenerateRD3560_8Mutation from '../../hooks/data-modifying/useGenerateRD3560_8Mutation';

import useUploadSignedMutation from '../../hooks/data-modifying/useUploadSignedMutation';
import useEditGrossRentCertificationMutation from './hooks.useEditGrossRentCertificationMutation';
import useRows from './hooks.useRows';
import useHeaders from './hooks.useHeaders';
import useParseResults from './hooks.useParseResults';
import useModalProps from '../../hooks/useModalProps';
import useCSVExport from './hooks.useCSVExport';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useActionsFirstColumnOrder from '../../hooks/useActionsFirstColumnOrder';
import useCheckboxColumn from '../../hooks/useCheckboxColumn';
import useAbortController from '../../hooks/useAbortController';
import useInitialFilterState from './hooks.useInitialFilterState';
import useDisableDownloadAllButton from './hooks.useDisableDownloadAllButton';
import usePlanningRangeFilter from './hooks.usePlanningRangeFilter';

import PropertyService from '../../services/propertyService';

// Constants
import {
  PROPERTY_PATH_MAP,
  COUNT_NAME,
  getRecertDuePropertyPathEntry,
} from './constantsV2';

export const RD_GROSS_RENT_EFFECTIVE_DATE_ERROR =
  'The Gross Rent effective date cannot be before the property move in date for the household.';

export const formatGenerateRD3560_8Error = (error: any): string => {
  const detail = error?.toString?.() ?? String(error);

  if (detail === RD_GROSS_RENT_EFFECTIVE_DATE_ERROR) {
    return `Failed to generate 3560_8 cert. ${detail}`;
  }

  return `Failed to generate 3560_8 ${detail}`;
};

const useFetchPropertyAffordableSetup = ({
  organizationId,
  propertyId,
}: {
  organizationId: string,
  propertyId: string,
}) => {
  const propertyService = new PropertyService();
  const abortControllerOptions = useAbortController();

  const queryKey = [
    'useFetchPropertyAffordableSetup',
    organizationId,
    propertyId,
  ];

  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      propertyService.getPropertyAffordableSetUp(
        organizationId,
        propertyId,
        abortControllerOptions,
      ),
    {
      enabled: true,
      refetchInterval: 60000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 59900,
    },
  );

  return {
    affordableSetup: data,
    isLoading,
  };
};

export const useManageCertifications = ({ NAME, intl }) => {
  const selectedProperty = useSelectedProperty();
  const [selectedCertificationType, setSelectedCertificationType] =
    useState('none');

  const name = useUniqueTableName(`${NAME}_${selectedCertificationType}`);
  const hasSelectedCertificationType = useMemo(
    () =>
      Boolean(
        selectedCertificationType?.length &&
          selectedCertificationType !== 'none',
      ),
    [selectedCertificationType],
  );

  const { affordableSetup } = useFetchPropertyAffordableSetup({
    organizationId: selectedProperty?.organizationId,
    propertyId: selectedProperty?.id,
  });

  // Fetch Certifications
  // memoize query args to prevent unnecessary re-renders
  const certificationQueryArgs = useMemo(() => {
    return {
      organizationId: selectedProperty?.organizationId,
      propertyId: selectedProperty?.id,
      certificationType: selectedCertificationType,
      options: {
        enabled: Boolean(
          selectedProperty?.id &&
            selectedProperty?.organizationId &&
            hasSelectedCertificationType,
        ),
      },
    };
  }, [
    selectedProperty,
    selectedCertificationType,
    hasSelectedCertificationType,
  ]);
  const {
    data: {
      results,
      summary,
      signed59ADocumentType,
      signed35608DocumentType,
      propertyAffordablePrograms: summaryAffordablePrograms,
    },
    isLoading,
    refetch: refetchCertifications,
  } = useCertificationsQuery(certificationQueryArgs);

  const {
    grossRentEnhancements: grossRentEnhancementsFlag,
    flagCertsForReview,
    completeCertificationVisibility = false,
  } = useFlags();

  // Parse results
  const parsedResults = useParseResults({
    results,
    selectedCertificationType,
    flagCertsForReview,
  });

  // Handle checkboxes column
  const { checkboxList, handleSelectRow, handleSelectDeSelectAllRows } =
    useCheckboxColumn({
      results: parsedResults,
      featureFlag: grossRentEnhancementsFlag,
    });

  // Manage Headers
  const headers = useHeaders({
    results: parsedResults,
    intl,
    selectedCertificationType,
    grossRentEnhancementsFlag,
    checkBoxListProps: {
      handleSelectDeSelectAllRows,
    },
  });

  const initialFilterState = useInitialFilterState({
    selectedCertificationType,
    flagCertsForReview,
  });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    initialFilterState,
    headers,
  });

  const selectedPropertyPathMap = useMemo(() => {
    const baseMap = PROPERTY_PATH_MAP[selectedCertificationType];
    if (!baseMap?.recertDue) return baseMap;
    return {
      ...baseMap,
      recertDue: getRecertDuePropertyPathEntry(),
    };
  }, [selectedCertificationType]);

  const sortedAndFilteredResults = useTableFilterSortData({
    results: parsedResults,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP: selectedPropertyPathMap,
  });

  // ENG-30054: "baseline" results for the Recerts tab's PlanningRangeFilter
  // per-bucket counts - every currently-active filter applied EXCEPT
  // `recertDue` itself, so a bucket's live count reflects "how many rows
  // would match if this bucket were also selected", regardless of which
  // other buckets are already selected (an unselected bucket never zeroes
  // out, a selected bucket's count doesn't shrink because of siblings).
  const baselineFilterState = useMemo(() => {
    const { recertDue, ...rest } = filterState ?? {};
    return rest;
  }, [filterState]);

  const baselineResults = useTableFilterSortData({
    results: parsedResults,
    order,
    orderBy,
    filterState: baselineFilterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP: selectedPropertyPathMap,
  });

  const propertyAffordablePrograms =
    selectedProperty?.pap?.map(
      ({ masterAffordableProgram: { name } }) => name,
    ) ?? [];

  const planningRangeOptions = usePlanningRangeFilter({
    baselineResults,
    propertyAffordablePrograms,
  });

  const selectedPlanningRange = filterState?.recertDue ?? [];

  const onPlanningRangeChange = useCallback(
    (value) => handleFilterChange('recertDue', value),
    [handleFilterChange],
  );

  const handleDownloadDocument = useDownloadDocumentMutation({
    organizationId: selectedProperty?.organizationId,
    propertyId: selectedProperty?.id,
    options: {
      enabled: Boolean(
        selectedProperty?.id &&
          selectedProperty?.organizationId &&
          hasSelectedCertificationType,
      ),
      onError: (error) => {
        toastr.error(
          'Error',
          `Failed to download document ${error.toString()}`,
        );
      },
    },
  });

  const modalProps = useModalProps();

  const handleGenerate59A = useGenerate59AMutation({
    organizationId: selectedProperty?.organizationId,
    propertyId: selectedProperty?.id,
    options: {
      enabled: Boolean(
        selectedProperty?.id &&
          selectedProperty?.organizationId &&
          hasSelectedCertificationType,
      ),
      onSuccess: () => {
        toastr.success('Success', 'Successfully generated 59A');
        refetchCertifications();
      },
      onError: (error) => {
        toastr.error('Error', `Failed to generate 59A ${error.toString()}`);
      },
    },
  });

  const handleGenerateRD3560_8 = useGenerateRD3560_8Mutation({
    organizationId: selectedProperty?.organizationId,
    propertyId: selectedProperty?.id,
    options: {
      enabled: Boolean(
        selectedProperty?.id &&
          selectedProperty?.organizationId &&
          hasSelectedCertificationType,
      ),
      onSuccess: () => {
        toastr.success('Success', 'Successfully generated 3560_8');
        refetchCertifications();
      },
      onError: (error) => {
        toastr.error('Error', formatGenerateRD3560_8Error(error));
      },
    },
  });

  const uploadSignedMutation = useUploadSignedMutation({
    organizationId: selectedProperty?.organizationId,
    propertyId: selectedProperty?.id,
    options: {
      enabled: Boolean(
        selectedProperty?.id &&
          selectedProperty?.organizationId &&
          hasSelectedCertificationType,
      ),
      onSuccess: () => {
        toastr.success(
          'Success',
          'The signed document was uploaded successfully.',
        );
        modalProps.handleClose();
        refetchCertifications();
      },
      onError: (error) => {
        toastr.error(
          'Error',
          `Failed to upload the signed document. ${error.toString()}`,
        );
      },
    },
  });

  const editGrossRentCertificationMutation =
    useEditGrossRentCertificationMutation({
      organizationId: selectedProperty?.organizationId,
      propertyId: selectedProperty?.id,
      options: {
        enabled: Boolean(
          selectedProperty?.id &&
            selectedProperty?.organizationId &&
            hasSelectedCertificationType,
        ),
        onSuccess: () => {
          toastr.success(
            'Success',
            'Successfully edited gross rent certification',
          );
          modalProps.handleClose();
          refetchCertifications();
        },
        onError: (error) => {
          toastr.error(
            'Error',
            `Failed to edit gross rent certification ${error.toString()}`,
          );
        },
      },
    });

  const rows = useRows({
    handleDownloadDocument,
    handleGenerate59A,
    modalProps,
    sortedAndFilteredResults,
    selectedCertificationType,
    propertySettings: {
      completeGRCertsWithoutSigned59As:
        selectedProperty?.setup?.HUDCompleteGRCertsWithoutSigned59As,
    },
    grossRentEnhancementsFlag,
    checkBoxListProps: {
      checkboxList,
      handleSelectRow,
    },
    handleGenerateRD3560_8,
  });

  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  const { onCSVButtonClick } = useCSVExport({
    selectedCertificationType,
    filteredHeaders,
    rows,
    hasAnyFilters: Boolean(Object.keys(filterState)?.length),
  });

  const disableDownloadAllButton = useDisableDownloadAllButton({
    selectedCertificationType,
    sortedAndFilteredResults,
  });

  return {
    affordableSetup,
    allColumnsHidden,
    columnOptions,
    completeCertificationVisibility,
    count: rows.length ?? 0,
    countName: COUNT_NAME,
    dateState,
    disableDownloadAllButton,
    editGrossRentCertificationMutation,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    hasSelectedCertificationType,
    isLoading,
    modalProps,
    name,
    onCSVButtonClick,
    onPlanningRangeChange,
    order,
    orderBy,
    planningRangeOptions,
    propertyAffordablePrograms,
    refetchCertifications,
    rows,
    searchState,
    selectedCertificationType,
    selectedColumns,
    selectedPlanningRange,
    setSelectedCertificationType,
    signed59ADocumentType,
    signed35608DocumentType,
    sortedAndFilteredResults,
    summary,
    summaryAffordablePrograms,
    totalCount: results?.length ?? 0,
    uploadSignedMutation,
  };
};
